import { useMutation, useQueryClient } from '@tanstack/react-query'
import { usePaginatedQuery } from './usePaginatedQuery'
import { useAuth } from '@/contexts/AuthContext'
import { accountApiService, accountQueries } from '@/services/accountApi'
import type { ApiKey, ApiKeyListResponse } from '@/types/account'

export const API_KEYS_PAGE_SIZE = 5

export const useApiKeysQuery = () => {
  const { isAuthenticated } = useAuth()

  return usePaginatedQuery<ApiKeyListResponse, ApiKey>({
    queryKey: accountQueries.apiKeysBase,
    queryFn: (page, pageSize) => accountApiService.getApiKeys(page, pageSize),
    transform: (response) => ({
      items: response.apiKeys,
      totalItems: response.total,
      totalPages: Math.ceil(response.total / response.pageSize),
    }),
    pageSize: API_KEYS_PAGE_SIZE,
    enabled: isAuthenticated,
  })
}

export const useCreateApiKey = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (name: string) => accountApiService.createApiKey(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountQueries.apiKeysBase })
    },
  })
}

export const useDeleteApiKey = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => accountApiService.deleteApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountQueries.apiKeysBase })
    },
  })
}
